import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Search, X } from "lucide-react";
import { CampaignLogStatus, CampaignLogStatusList } from "@bt-common/schemas/bt/campaign-log";

interface CampaignLogFiltersProps {
    searchQuery: string;
    statusFilter: CampaignLogStatus | 'all';
    sourceFilter: 'backend' | 'client' | 'all';
    clientTypeFilter: 'gateway' | 'whatsapp' | 'frontend' | 'all';
    onSearchChange: (query: string) => void;
    onStatusFilterChange: (status: CampaignLogStatus | 'all') => void;
    onSourceFilterChange: (source: 'backend' | 'client' | 'all') => void;
    onClientTypeFilterChange: (type: 'gateway' | 'whatsapp' | 'frontend' | 'all') => void;
    onClearFilters: () => void;
}

export function CampaignLogFilters({
    searchQuery,
    statusFilter,
    sourceFilter,
    clientTypeFilter,
    onSearchChange,
    onStatusFilterChange,
    onSourceFilterChange,
    onClientTypeFilterChange,
    onClearFilters,
}: CampaignLogFiltersProps) {
    const hasActiveFilters = 
        searchQuery !== '' || 
        statusFilter !== 'all' || 
        sourceFilter !== 'all' || 
        clientTypeFilter !== 'all';

    return (
        <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
                <label className="text-sm font-medium mb-1.5 block">Search</label>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                        placeholder="Search by log message or status..."
                        value={searchQuery}
                        onChange={(e) => onSearchChange(e.target.value)}
                        className="pl-9"
                    />
                </div>
            </div>

            <div className="w-[180px]">
                <label className="text-sm font-medium mb-1.5 block">Status</label>
                <Select value={statusFilter} onValueChange={onStatusFilterChange}>
                    <SelectTrigger>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        {CampaignLogStatusList.map((status) => (
                            <SelectItem key={status} value={status}>
                                {status.replace('campaign-', '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="w-[180px]">
                <label className="text-sm font-medium mb-1.5 block">Source</label>
                <Select value={sourceFilter} onValueChange={onSourceFilterChange}>
                    <SelectTrigger>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Sources</SelectItem>
                        <SelectItem value="backend">Backend</SelectItem>
                        <SelectItem value="client">Client</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="w-[180px]">
                <label className="text-sm font-medium mb-1.5 block">Client Type</label>
                <Select value={clientTypeFilter} onValueChange={onClientTypeFilterChange}>
                    <SelectTrigger>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="frontend">Frontend</SelectItem>
                        <SelectItem value="whatsapp">WhatsApp</SelectItem>
                        <SelectItem value="gateway">Gateway</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {hasActiveFilters && (
                <Button
                    variant="outline"
                    size="sm"
                    onClick={onClearFilters}
                    className="mb-0"
                >
                    <X className="h-4 w-4 mr-1" />
                    Clear Filters
                </Button>
            )}
        </div>
    );
}

